import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiGift, FiFileText, FiStar, FiCalendar } = FiIcons;

const BonusSection = () => {
  const bonuses = [
    {
      icon: FiFileText,
      title: "แผ่นดวงประจำตัว",
      description: "ใบดวงชะตาที่คำนวณเฉพาะคุณ พร้อมคำอธิบายรายละเอียด",
      value: "มูลค่า 3,000 บาท"
    },
    {
      icon: FiStar,
      title: "ยันต์ \"ฮู้\" จีนเฉพาะตัว",
      description: "ยันต์เรียกทรัพย์โบราณที่สร้างเฉพาะสำหรับดวงชะตาของคุณ (รับได้ไม่เกิน 20 คนแรก)",
      value: "มูลค่า 12,000 บาท",
      special: true
    },
    {
      icon: FiCalendar,
      title: "ปฏิทินฤกษ์ประจำตัว",
      description: "ปฏิทินบอกวันเวลาที่เหมาะสมสำหรับการทำธุรกรรมสำคัญ",
      value: "มูลค่า 2,000 บาท"
    }
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-r from-purple-900/20 to-indigo-900/20 relative overflow-hidden">
      {/* Background decorative image */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394482125-blob" 
          alt="Background" 
          className="absolute bottom-0 left-0 w-full h-full object-cover" 
        />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <SafeIcon icon={FiGift} className="text-4xl text-amber-400" />
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              โบนัสพิเศษ
            </h2>
            <SafeIcon icon={FiGift} className="text-4xl text-amber-400" />
          </div>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            สิ่งที่คุณจะได้รับเพิ่มเติม <span className="text-amber-400 font-bold">ฟรี!</span>
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {bonuses.map((bonus, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`bg-gradient-to-br from-slate-800/70 to-slate-700/70 backdrop-blur-sm rounded-xl p-8 border ${
                bonus.special ? 'border-red-500/50 ring-2 ring-red-500/20' : 'border-purple-500/30'
              } hover:border-purple-500/50 transition-all duration-300 hover:transform hover:scale-105 relative overflow-hidden`}
            >
              {/* Special highlight for limited bonus */}
              {bonus.special && (
                <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-orange-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                  จำกัด 20 คน!
                </div>
              )}
              
              {/* Sparkle effect */}
              <div className="absolute top-4 left-4 text-2xl animate-pulse">✨</div>
              
              <div className="text-center">
                <div className={`w-16 h-16 bg-gradient-to-br ${
                  bonus.special ? 'from-red-500 to-orange-600' : 'from-purple-500 to-indigo-600'
                } rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <SafeIcon icon={bonus.icon} className="text-white text-2xl" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4">
                  {bonus.title}
                </h3>
                
                <p className="text-slate-300 mb-6 leading-relaxed">
                  {bonus.description}
                </p>
                
                <div className={`bg-gradient-to-r ${
                  bonus.special ? 'from-red-500/20 to-orange-600/20 border-red-500/30' : 'from-amber-500/20 to-yellow-600/20 border-amber-500/30'
                } rounded-lg p-3 border`}>
                  <p className={`${bonus.special ? 'text-red-400' : 'text-amber-400'} font-bold`}>
                    🎁 {bonus.value}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-purple-500/20 to-indigo-600/20 rounded-xl p-8 border border-purple-500/30 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-purple-400 mb-4">
              รวมมูลค่าโบนัสทั้งหมด
            </h3>
            
            <div className="text-center mb-6">
              <p className="text-3xl font-bold text-amber-400 mb-2">
                17,000 บาท
              </p>
              <p className="text-lg text-white">
                แต่คุณได้รับ <span className="text-green-400 font-bold">ฟรี!</span> เมื่อเข้าหลักสูตร
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-lg p-4 border border-red-500/30 mb-6">
              <p className="text-red-400 font-bold text-lg mb-2">
                ⚠️ ข้อจำกัดสำคัญ
              </p>
              <p className="text-white">
                ยันต์ "ฮู้" จีนเฉพาะตัว รับได้ไม่เกิน <span className="text-amber-400 font-bold">20 คนแรก</span><br />
                เนื่องจากต้องใช้เวลาสร้างนาน และต้องจองก่อนวันที่ <span className="text-red-400 font-bold">9 ก.ค.</span>
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-6 border border-amber-500/20">
              <p className="text-lg text-white mb-2">
                <span className="text-slate-400">ค่าหลักสูตร:</span> 
                <span className="text-amber-400 font-bold ml-2">5,500 บาท</span>
              </p>
              <p className="text-lg text-white mb-2">
                <span className="text-slate-400">โบนัสที่ได้:</span> 
                <span className="text-green-400 font-bold ml-2">17,000 บาท</span>
              </p>
              <div className="border-t border-slate-600 mt-4 pt-4">
                <p className="text-xl font-bold text-green-400">
                  คุณค่าที่ได้รับจริง: 22,500 บาท
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BonusSection;